import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Organization comes back from /auth/me alongside the user. We
  // expose it through the context so components (Layout, route
  // guards) can read feature-flag fields like opendental.enabled
  // without an extra API round-trip on every navigation.
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Token'ı localStorage'a kaydet
  const saveToken = (newToken) => {
    setToken(newToken);
    localStorage.setItem('token', newToken);
  };

  // Token'ı localStorage'dan sil
  const removeToken = () => {
    setToken(null);
    localStorage.removeItem('token');
  };

  // Giriş fonksiyonu
  const login = async (email, password) => {
    try {
      const response = await authAPI.login({ email, password });
      const { user: userData, token: userToken } = response.data;

      setUser(userData);
      saveToken(userToken);

      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'An error occurred while logging in'
      };
    }
  };

  // Çıkış fonksiyonu
  const logout = async () => {
    try {
      if (token) {
        await authAPI.logout();
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      removeToken();
    }
  };

  // Mevcut kullanıcı bilgilerini getir
  const getCurrentUser = async () => {
    try {
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await authAPI.getMe();
      setUser(response.data.user);
      setOrganization(response.data.organization || null);
    } catch (error) {
      console.error('Mevcut kullanıcı getirme hatası:', error);
      // Token geçersizse temizle
      removeToken();
      setUser(null);
      setOrganization(null);
    } finally {
      setLoading(false);
    }
  };

  // Token değiştiğinde kullanıcı bilgilerini getir
  useEffect(() => {
    getCurrentUser();
  }, [token]);

  const value = {
    user,
    organization,
    loading,
    token,
    login,
    logout,
    isAuthenticated: !!user && !!token,
    refreshUser: getCurrentUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 